import React, {ReactElement} from "react"
import {AllowedIncomingChecksColumns, IncomingChecksColumns} from "./IncomingCheck.types"
import {Resource} from "../../resources/common"
import {useNavigate} from "react-router-dom"
import {IncomingCheck} from "../../resources/incomingCheck"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import {BankName} from "../BankName/BankName"
import {kebabCase, startCase} from "lodash"
import numeral from "numeral"
import moment from "moment"
import ReactTooltip from "react-tooltip"
import classNames from "classnames"
import {useIsUnitUser} from "../../services/auth"
import {Org} from "../../resources/org"
import IdLink from "../IdLink/IdLink"

function OrgName({orgId, included}: {orgId: string | undefined | null; included?: Resource[]}) {
	if (included && orgId) {
		const org = included.find((r) => r.type == "org" && r.id === orgId) as Org
		if (org) {
			return <td>{org.attributes.name}</td>
		}
	}
	return <td></td>
}

interface IncomingCheckRowProps {
	incomingCheck: IncomingCheck
	includedColumns: AllowedIncomingChecksColumns[]
	refresh: () => void
	include?: Resource[]
	onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void
}

export default function IncomingCheckRow({
	incomingCheck: incomingCheck,
	includedColumns,
	include,
	onClick,
}: IncomingCheckRowProps) {
	const navigate = useNavigate()
	const id = incomingCheck.id
	const org = incomingCheck.relationships.org?.data.id
	const bank = incomingCheck.relationships.bank.data.id
	const customerId = incomingCheck.relationships.customer?.data.id
	const accountId = incomingCheck.relationships.account?.data.id
	const status = incomingCheck.attributes.status
	const createdAt = incomingCheck.attributes.createdAt
	const amount = incomingCheck.attributes.amount

	const customer =
		customerId && include
			? include.find(
					(r) => (r.type == "individualCustomer" || r.type == "businessCustomer") && r.id == customerId.toString()
			  )
			: undefined
	let customerName: string | undefined = undefined

	if (customer) {
		if (customer.type == "individualCustomer") {
			customerName = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
		} else if (customer.type == "businessCustomer") {
			customerName = customer.attributes.name
		}
	}

	const contentColumns: Partial<Record<AllowedIncomingChecksColumns, ReactElement>> = {}

	if (includedColumns.includes(IncomingChecksColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell>
				<IdLink className="data-table-id" href={`/incoming-checks/${id}`}>
					{id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingChecksColumns.org)) {
		contentColumns["Org"] = <OrgName orgId={org} included={include} />
	}

	if (useIsUnitUser() && includedColumns.includes(IncomingChecksColumns.bank) && bank) {
		contentColumns["Bank"] = (
			<DataTableCell>
				<BankName bankId={bank.toString()} included={include} />
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingChecksColumns.customer)) {
		contentColumns["Customer"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (customer) {
							navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
						}
					}}
				>
					{customerName}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingChecksColumns.accountId)) {
		contentColumns["Account"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (accountId) {
							navigate(`/accounts/${accountId}`)
						}
					}}
				>
					{accountId}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingChecksColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell>
				<span className="incoming-checks-amount">{numeral(amount / 100).format("$0,0.00")}</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingChecksColumns.status)) {
		const tooltipValue = undefined

		contentColumns["Status"] = (
			<DataTableCell>
				<div
					data-tip="React-tooltip"
					data-for={`status-reason-tooltip-${id}`}
					className={classNames("incoming-check-status", kebabCase(status))}
				>
					{startCase(status)}

					<ReactTooltip
						className="tooltip-info"
						id={`status-reason-tooltip-${id}`}
						disable={!tooltipValue}
						effect="solid"
					>
						{tooltipValue}
					</ReactTooltip>
				</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingChecksColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(createdAt).format("L LT")}</DataTableCell>
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return <DataTableRow onClick={(e) => onClick && onClick(e)}>{content}</DataTableRow>
}
